import { gql } from "@apollo/client"

export const DRAFT_PLAN_DATA_SELECTION = `
plan {
  id
  program
  subject
  scope
  name
  subType
  targetSeconds
  totalSeconds
  sections {
    id
    type
    name
    totalSeconds
    targetSeconds
    canSetTargetSeconds
    breakBefore
    breakAfter
    items{
      id
      contentId
      name
      totalSeconds
      teachLive
      completed
      breakBefore
      breakAfter
      addedBy
      addSeq
      selectedMicroSnapCount
      maxMicroSnapCount
    }
  }
  meta{
    canUndo
    canRedo
    canAddPracticeTest
    canAddBootCamp
    canAddMasteryLesson
    remainingPracticeTestContentSeconds
    remainingMasteryContentSeconds
    studentCanDoOutOfOrder
    studentCanDoOutOfOrderDefault
    studentCanDoOutOfOrderRecommended
    studentCanResetMasteryLessons
    studentCanResetMasteryLessonsDefault
    studentCanResetMasteryLessonsRecommended
    studentCanPauseTests
    studentCanPauseTestsDefault
    studentCanPauseTestsRecommended
    studentTestTimingValue
    studentTestTimingType
    studentTestTimingValueDefault
    studentTestTimingValueRecommended
  }
}
`

const COMMON_PUBLISHED_PLAN_SELECTION = `
id
plan_guid
program
subject
refScope{
  scope
  district_id
  school_id
  teacher_id
  class_id
  student_id
}
name
subType
targetSeconds
totalSeconds
sections{
  id
  section_guid
  type
  name
  totalSeconds
  canSetTargetSeconds
  targetSeconds
  percentComplete
  percentCorrect
  breakBefore
  breakAfter
  items{
    item_guid
    name
    contentId
    totalSeconds
    teachLive
    completed
    breakBefore
    breakAfter
    canReset
    canRestore
    resetTimestamp
    percentComplete
    percentCorrect
    averageScore
    gradeLevel
    addedBy
    addSeq
    level
    selectedMicroSnapCount
    meta{
      canClearTeachLive
      canMarkTeachLive
      canClearComplete
      canMarkComplete
      canDownloadResource
    }
  }
}
meta{
  canEdit
  isRelated
  isRecommended
  displayName
  tags
}
`

// standards, maxMicroSnapCount, questionCount, snapVideosCount, interactionCount go to meta. All optional, may or may not exist.
export const NO_PLAN_AVAILABLE_ITEM_SELECTION = `
  id
  name
  type
  resources

  totalSeconds
  hasPreview
  hasDetails
  description
  standards
  meta
  short_name
  ref_id
  parent_id
  url
`

export const PLAN_AVAILABLE_ITEM_SELECTION = `
  ${NO_PLAN_AVAILABLE_ITEM_SELECTION}

  canTeachLive
  isTeachLive
  isInPlan
  planSectionId
  selectedMicroSnapCount
`

export const CATEGORIZED_PLAN_AVAILABLE_ITEM_SELECTION = `
masteryLessons{
  ${PLAN_AVAILABLE_ITEM_SELECTION}
}
practiceTests{
  ${PLAN_AVAILABLE_ITEM_SELECTION}

}
`

export const SECTION_AVAILABLE_ITEM_SELECTION = `
  ${PLAN_AVAILABLE_ITEM_SELECTION}

  canAdd
  canRemove
  canMoveIn
  canReplace
`

export const PUBLISHED_PLAN_DATA_SELECTION = `
plan{
  ${COMMON_PUBLISHED_PLAN_SELECTION}
}
`

export const PUBLISHED_PLANS_DATA_SELECTION = `
plans{
  ${COMMON_PUBLISHED_PLAN_SELECTION}
}
`

const ENTITY_PLAN_INFO = `
entityName
entityId
planId
planType
planLevel
planDisplayName
planTags
`

const ENTITY_INFO = `
entityName
entityId
`

export const GET_DRAFT_PLAN = gql`
  query getDraftPlan($plan_id: String!){
    getDraftPlan(plan_id: $plan_id){
      ${DRAFT_PLAN_DATA_SELECTION}
    }
  }
`

export const GET_CURRENT_PLAN_BY_REFSCOPE = gql`
  query getCurrentPlanByRefScope(
            $program: Program!
            $subject: String!
            $refScope: refScope!
            $requirePercentSummary: Boolean
        ){
          getCurrentPlanByRefScope(
            program: $program
            subject: $subject
            refScope: $refScope
            requirePercentSummary: $requirePercentSummary
          ){
            ${PUBLISHED_PLAN_DATA_SELECTION}
          }
        }
`

export const GET_RECOMMENDED_PLAN_BY_REFSCOPE = gql`
  query getRecommendedPlanByRefScope(
            $program: Program!
            $subject: String!
            $refScope: refScope!
            $requirePercentSummary: Boolean
        ){
          getRecommendedPlanByRefScope(
            program: $program
            subject: $subject
            refScope: $refScope
            requirePercentSummary: $requirePercentSummary
          ){
            ${PUBLISHED_PLAN_DATA_SELECTION}
          }
        }
`
export const GET_CUSTOM_PLAN_AT_REFSCOPE = gql`
  query getCustomPlanAtRefScope(
    $program: Program!
    $subject: String!
    $refScope: refScope!
    $requirePercentSummary: Boolean
  ) {
    getCustomPlanAtRefScope(
      program: $program
      subject: $subject
      refScope: $refScope
      requirePercentSummary: $requirePercentSummary
      ){
      ${PUBLISHED_PLAN_DATA_SELECTION}
    }
  }
`
export const GET_PLANS = gql`
  query getCurrentPlanForEachSubjectByRefScope(
            $program: Program!
            $refScope: refScope!
            $requirePercentSummary: Boolean
        ){
          getCurrentPlanForEachSubjectByRefScope(
            program: $program
            refScope: $refScope
            requirePercentSummary: $requirePercentSummary
          ){
            ${PUBLISHED_PLANS_DATA_SELECTION}
          }
        }
`

export const GET_SECTION_AVAILABLE_ITEMS = gql`
  query getSectionAvailableItems($plan_id: String!, $section_id: String!) {
    getSectionAvailableItems(
      data: { plan_id: $plan_id, section_id: $section_id }
    ) {
      ${SECTION_AVAILABLE_ITEM_SELECTION}
    }
  }
`

export const GET_PLAN_AVAILABLE_ITEMS = gql`
  query getPlanAvailableItems($plan_id: String!) {
    getPlanAvailableItems(
      data: { plan_id: $plan_id}
    ) {
      ${CATEGORIZED_PLAN_AVAILABLE_ITEM_SELECTION}
    }
  }
`

export const GET_ITEM_PREVIEW = gql`
  query getItemPreview($content_id: String!, $plan_id:String, $section_id:String) {
    getItemPreview(content_id: $content_id, plan_id: $plan_id, section_id: $section_id) {
      ${SECTION_AVAILABLE_ITEM_SELECTION}
    }
  }
`

export const GET_PLAN_HISTORY = gql`
  query getPlanHistory(
    $program: Program!
    $subject: String!
    $refScope: refScope!
  ) {
    getPlanHistory(program: $program, subject: $subject, refScope: $refScope)
  }
`
export const GET_ADMIN_RELEVANT_PLAN_INFO = gql`
  query getAdminRelevantPlanInfo(
    $program: Program!
    $subject: String!
    $refScope: refScope!
  ) {
    getAdminRelevantPlanInfo(
      program: $program
      subject: $subject
      refScope: $refScope
    ){
      recommendedPlanId
      recommendedPlanLevel
      district{
        ${ENTITY_PLAN_INFO}
      }
      school{
        ${ENTITY_PLAN_INFO}
      }
      classCount
    }
  }
`

export const GET_TEACHER_RELEVANT_PLAN_INFO = gql`
  query getTeacherRelevantPlanInfo(
    $program: Program!
    $subject: String!
    $refScope: refScope!
  ) {
    getTeacherRelevantPlanInfo(
      program: $program
      subject: $subject
      refScope: $refScope
    ){
      recommendedPlanId
      recommendedPlanLevel
      teacherPlanId
      teacherplanTags
      classes{
        ${ENTITY_INFO}
      }
      students{
        ${ENTITY_INFO}
      }
    }
  }
`

// no field selection, because it only contain one return
export const GET_ASSIGNED_SUBJECT_BY_REFSCOPE = gql`
  query getAssignedSubjectsByRefScope(
    $program: Program!
    $refScope: refScope!
  ) {
    getAssignedSubjectsByRefScope(program: $program, refScope: $refScope)
  }
`

// test one
export const LIST_STUDY_PLANS = gql`
  query listStudyPlans(
    $status: listStudyplanStatusOption!
    $fields: listStudyplanFieldsOption!
  ) {
    listStudyPlans(status: $status, fields: $fields)
  }
`

export const GET_GLOBAL_PLAN_SETTINGS = gql`
  query getGlobalPlanSettings($data: ProgramRefScopeInput) {
    getGlobalPlanSettings(data: $data) {
      studentCanDoOutOfOrder
      studentCanResetMasteryLessons
      studentCanPauseTests
      studentCanDoOutOfOrderDefault
      studentCanResetMasteryLessonsDefault
      studentCanPauseTestsDefault
      studentTestTimingType
      studentTestTimingValue
      studentTestTimingValueDefault
    }
  }
`

// DF Disabled until further notice
// export const GET_GUID_PLAN_SETTINGS = gql`
//   query getGuidPlanSettings($data: PlanGuidMetaInput) {
//     getGuidPlanSettings(data: $data) {
//       studentCanDoOutOfOrder
//       studentCanResetMasteryLessons
//     }
//   }
// `

export const GET_STUDENT_MASTERY_LESSON_SNAPS = gql`
  query getStudentMasteryLessonSnaps(
    $program: Program!
    $subject: String!
    $contentId: String!
    $contentLibrary: Boolean
    $assignmentId: String
  ) {
    getStudentMasteryLessonSnaps(
      data: {
        program: $program
        subject: $subject
        contentId: $contentId
        contentLibrary: $contentLibrary
        assignmentId: $assignmentId
      }
    ) {
      items {
      ${NO_PLAN_AVAILABLE_ITEM_SELECTION}
      }
    }
  }
`

export const GET_RESET_CLASS_MASTERY_LESSONS_AFFECTED_COUNT = gql`
  query getResetClassMasteryLessonsAffectedCount(
    $data: ResetRestoreMasteryLessonsRequest!
  ) {
    getResetClassMasteryLessonsAffectedCount(data: $data)
  }
`

export const GET_STUDENT_STUDY_PLAN_STATS = gql`
  query getStudentStudyPlanStats($data: StudentStudyPlanStatsInput!) {
    getStudentStudyPlanStats(data: $data) {
      headers {
        id
        name
      }
      students {
        id
        name
        data {
          id
          value
          pass
        }
        completed
      }
      pageCount
      sortKeys
    }
  }
`
